// =============================================================================
// src/features/mapa-ia/mapa-ia-markup.ts — Página fullscreen do "Mapa de IA".
//
// Renderiza o registro src/data/ai-surfaces agrupado por domínio: para cada
//   superfície mostra o badge (status), metadados, insumos e a tabela campo-a-campo
//   (uiField → backendField : tipo [transform]). É a versão navegável do contrato
//   que o backend usa para popular o front; espelhada em AI-CONTRACT.md.
// =============================================================================

import type { AiFieldMapping, AiSurfaceEntry } from '../../data/ai-surface-types';
import { AI_SURFACES, groupAiSurfacesByDomain, type AiSurfaceGroup } from '../../data/ai-surfaces';
import { renderAiBadge } from '../../views/shared/ai-badge';
import { escapeHtml } from '../../app/dom';

/** Contagem de superfícies por status, para a legenda do cabeçalho. */
function statusCount(status: AiSurfaceEntry['status']): number {
  return AI_SURFACES.filter((s) => s.status === status).length;
}

/** Cabeçalho da página: título, intenção e legenda de status. */
function headHtml(): string {
  return `<header class="u-mapa-ia-head">
    <div class="u-mapa-ia-eyebrow">Contrato de IA <span class="u-mapa-ia-sep"></span> front ↔ backend AtalaIA</div>
    <h1 class="u-mapa-ia-title">Mapa de IA</h1>
    <p class="u-mapa-ia-lede">Toda superfície gerada ou processada por IA, com o mapeamento campo-a-campo da saída do backend que a popula.</p>
    <div class="u-mapa-ia-legend">
      <span class="u-ai-badge u-ai-badge--live"><i class="ti ti-sparkles" aria-hidden="true"></i> ao vivo ${statusCount('live')}</span>
      <span class="u-ai-badge u-ai-badge--partial"><i class="ti ti-sparkles" aria-hidden="true"></i> parcial ${statusCount('partial')}</span>
      <span class="u-ai-badge u-ai-badge--mock"><i class="ti ti-sparkles" aria-hidden="true"></i> simulado ${statusCount('mock')}</span>
    </div>
  </header>`;
}

/** Linha da tabela de contrato de um campo (uiField → backendField : tipo). */
function fieldRowHtml(field: AiFieldMapping): string {
  const transform = field.transform ? ` <span class="u-mapa-ia-xform">${escapeHtml(field.transform)}</span>` : '';
  return `<tr>
    <td class="u-mapa-ia-uf">${escapeHtml(field.uiField)}</td>
    <td class="u-mapa-ia-bf">${escapeHtml(field.backendField)}</td>
    <td class="u-mapa-ia-ft">${escapeHtml(field.type)}${transform}</td>
  </tr>`;
}

/** Tabela campo-a-campo de uma superfície. */
function fieldsTableHtml(fields: readonly AiFieldMapping[]): string {
  return `<table class="u-mapa-ia-fields">
    <thead><tr><th>UI</th><th>Backend</th><th>Tipo</th></tr></thead>
    <tbody>${fields.map(fieldRowHtml).join('')}</tbody>
  </table>`;
}

/** Metadados de uma superfície (kind · camada · fonte · renderRef). */
function surfaceMetaHtml(surface: AiSurfaceEntry): string {
  return `<div class="u-mapa-ia-meta">
    <code class="u-mapa-ia-id">${escapeHtml(surface.id)}</code>
    <span>${escapeHtml(surface.aiKind)}</span><span>${escapeHtml(surface.backendLayer)}</span>
    <code class="u-mapa-ia-src">${escapeHtml(surface.backendSource.name)}</code>
  </div>
  <div class="u-mapa-ia-loc">${escapeHtml(surface.location)} · <code>${escapeHtml(surface.renderRef)}</code></div>
  <div class="u-mapa-ia-inputs">Insumos: ${surface.inputs.map(escapeHtml).join(' · ')}</div>`;
}

/** Card completo de uma superfície (cabeçalho + badge + meta + tabela). */
function surfaceCardHtml(surface: AiSurfaceEntry): string {
  return `<article class="u-mapa-ia-card" data-ai-map-surface="${surface.id}">
    <header class="u-mapa-ia-card-head">
      <span class="u-mapa-ia-card-label">${escapeHtml(surface.label)}</span>
      ${renderAiBadge(surface)}
    </header>
    ${surfaceMetaHtml(surface)}
    ${fieldsTableHtml(surface.fields)}
  </article>`;
}

/** Seção de um domínio (título + contagem + cards). */
function groupSectionHtml(group: AiSurfaceGroup): string {
  return `<section class="u-mapa-ia-group">
    <h2 class="u-mapa-ia-group-title">${escapeHtml(group.label)} <span class="u-mapa-ia-group-count">${group.surfaces.length}</span></h2>
    ${group.surfaces.map(surfaceCardHtml).join('')}
  </section>`;
}

/**
 * Markup completo da rota fullscreen do Mapa de IA (montada em #routeLayer).
 *
 * @example
 *   mountPoint('routeLayer').innerHTML += buildMapaIaPageMarkup();
 */
export function buildMapaIaPageMarkup(): string {
  const groups = groupAiSurfacesByDomain().map(groupSectionHtml).join('');
  return `<div id="u-mapa-ia-page" class="u-mapa-ia-page" aria-hidden="true">
  <div class="u-mapa-ia-shell">
    ${headHtml()}
    ${groups}
    <footer class="u-mapa-ia-foot">Gerado de <code>src/data/ai-surfaces</code> (fonte única) · espelhado em <code>AI-CONTRACT.md</code></footer>
  </div>
</div>`;
}
